import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, Plus, Trash2, MoreHorizontal } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatDateHu } from "@/components/DeductionFormDialog";
import { Th, Td } from "../tabHelpers";

/* -------------------- Form dialog -------------------- */
const emptyForm = { reason: "", description: "", issuer: "", issued_at: "" };

export function DisciplinaryFormDialog({ open, onOpenChange, employee, onSaved }) {
  const { user } = useAuth();
  const currentUserName = user?.name || user?.email || "—";
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyForm, issuer: currentUserName });
    setErr("");
  }, [open, currentUserName]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.reason.trim()) {
      setErr("Az indok megadása kötelező.");
      return;
    }
    setSaving(true);
    try {
      if (!employee?._id) throw new Error("Hiányzó munkavállaló.");
      const payload = {
        issuer: form.issuer.trim(),
        reason: form.reason.trim(),
        description: form.description.trim() || null,
        issued_at: form.issued_at,
      };
      const { data } = await api.post(
        `/employees/${employee._id}/disciplinaries`,
        payload
      );
      toast.success("Fegyelmi bejegyzés rögzítve.");
      onOpenChange(false);
      if (onSaved) onSaved(data);
    } catch (error) {
      setErr(formatApiError(error?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="disciplinary-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Új fegyelmi bejegyzés</DialogTitle>
          <DialogDescription>
            Rögzítsd a fegyelmi eljárás indokát és részletes leírását.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Indok *" testId="disciplinary-reason">
            <Input
              value={form.reason}
              onChange={(e) =>
                setForm((f) => ({ ...f, reason: e.target.value }))
              }
              placeholder="Pl. munkahelyi késés, szabálysértés…"
              maxLength={300}
            />
          </Field>

          <Field label="Leírás" testId="disciplinary-description">
            <Textarea
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              rows={4}
              placeholder="A fegyelmi eljárás részletes ismertetése."
            />
          </Field>

          <Field label="Időpont *" testId="disciplinary-issued-at">
            <DateInput
              value={form.issued_at}
              onChange={(v) =>
                setForm((f) => ({ ...f, issued_at: v }))
              }
            />
          </Field>

          <Field label="Kiadó (automatikus)" testId="disciplinary-issuer">
            <div
              data-testid="disciplinary-issuer-display"
              className="flex h-9 items-center rounded-md border border-border bg-muted/40 px-3 text-sm"
            >
              <span className="truncate">{form.issuer || "—"}</span>
            </div>
          </Field>

          {err && (
            <div
              data-testid="disciplinary-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="disciplinary-cancel"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="disciplinary-submit">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : (
                "Rögzítés"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Section list + table -------------------- */
export function DisciplinarySection({
  items,
  loading,
  onCreate,
  onSelect,
  onDelete,
}) {
  return (
    <section data-testid="disciplinary-section" className="space-y-5">
      <header className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-muted-foreground/80">
              <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
                Fegyelmi
              </span>
            </div>
            <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-foreground sm:text-[22px]">
              Fegyelmi bejegyzések
            </h2>
          </div>
          <Button
            size="sm"
            data-testid="new-disciplinary-button"
            onClick={onCreate}
            className="self-start"
          >
            <Plus className="h-4 w-4" />
            Új fegyelmi
          </Button>
        </div>
      </header>

      <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Fegyelmi bejegyzések
            betöltése…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="disciplinary-empty-state"
            className="flex flex-col items-center gap-3 px-6 py-14 text-center"
          >
            <div className="grid h-11 w-11 place-items-center rounded-full border border-border bg-background text-muted-foreground/80">
              <ShieldAlert className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-medium">Nincs fegyelmi bejegyzés</p>
            <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">
              Itt jelennek meg a dolgozó fegyelmi eljárásai és figyelmeztetései.
            </p>
            <Button variant="outline" size="sm" className="mt-2 h-8" onClick={onCreate}>
              <Plus className="h-3.5 w-3.5" /> Új fegyelmi
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="disciplinary-table">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                  <Th>Indok</Th>
                  <Th>Kiadó</Th>
                  <Th>Időpont</Th>
                  <Th className="pr-4 text-right">Műveletek</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it._id}
                    data-testid={`disciplinary-row-${it._id}`}
                    onClick={() => onSelect && onSelect(it)}
                    className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                  >
                    <Td>
                      <span className="font-medium">{it.reason}</span>
                      {it.description && (
                        <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                          {it.description}
                        </p>
                      )}
                    </Td>
                    <Td>{it.issuer}</Td>
                    <Td className="text-muted-foreground">
                      {formatDateHu(it.issued_at)}
                    </Td>
                    <Td className="pr-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`disciplinary-actions-${it._id}`}
                            aria-label="Fegyelmi műveletek"
                            className="ml-auto grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            data-testid={`delete-disciplinary-${it._id}`}
                            onClick={() => onDelete(it)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            Visszavonás
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

/* -------------------- Detail dialog -------------------- */
export function DisciplinaryDetailDialog({ open, onOpenChange, item, onDelete }) {
  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="disciplinary-detail-dialog"
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>Fegyelmi bejegyzés részletei</DialogTitle>
          <DialogDescription>
            A fegyelmi eljárás teljes adatai.
          </DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <InfoRow label="Indok" wide>
            <span className="font-medium">{item.reason}</span>
          </InfoRow>
          <InfoRow label="Kiadó">{item.issuer || "—"}</InfoRow>
          <InfoRow label="Időpont">{formatDateHu(item.issued_at)}</InfoRow>
          {item.description && (
            <InfoRow label="Leírás" wide>
              <span className="whitespace-pre-wrap">{item.description}</span>
            </InfoRow>
          )}
        </dl>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="disciplinary-detail-delete"
            onClick={() => onDelete(item)}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Visszavonás
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Bezárás
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function InfoRow({ label, wide, children }) {
  return (
    <div className={wide ? "space-y-1 sm:col-span-2" : "space-y-1"}>
      <p className="text-[10.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <div className="text-[13.5px]">{children}</div>
    </div>
  );
}
